<script setup lang="ts">
import { customValidators } from "@/i18n/utils/i18n-validators";
import type { ApiError } from "@shopware/api-client";
import { useVuelidate } from "@vuelidate/core";

const { required, minLength, requiredIf, email, hasUpperAndLowerCase } =
  customValidators();
const { pushInfo, pushError } = useNotifications();
const { t } = useI18n();
const { register } = useUser();
const { push } = useRouter();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const { getCountries, getStatesForCountry } = useCountries();
const { getSalutations } = useSalutations();
const { refreshSessionContext } = useSessionContext();
const { refreshCart } = useCart();
const state = reactive({
  salutationId: "",
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  guest: false,
  accountType: "private" as "private" | "business",
  vatIds: [null] as (string | null)[],
  billingAddress: {
    customerId: "",
    firstName: "",
    id: "",
    lastName: "",
    street: "",
    zipcode: "",
    city: "",
    countryId: "",
    phoneNumber: "",
    department: "",
    company: null as string | null,
    customFields: {
      custom_address_field_phone_number_2: ""
    }
  },
  useDifferentShippingAddress: false,
  shippingAddress: {
    salutationId: "",
    firstName: "",
    lastName: "",
    company: "",
    department: "",
    street: "",
    zipcode: "",
    city: "",
    countryId: "",
    phoneNumber: "",
  },
  acceptedDataProtection: true,
  storefrontUrl: "",
  securityFormField: "",
});

const rules = computed(() => {
  const passwordRules = state.guest
    ? {}
    : {
        required,
        minLength: minLength(8),
        hasUpperAndLowerCase,
      };

  return {
    accountType: {
      required,
    },
    salutationId: {
      required,
    },
    vatIds: {},
    firstName: {
      required,
      minLength: minLength(1),
    },
    lastName: {
      required,
      minLength: minLength(1),
    },
    email: {
      required,
      email,
    },
    password: passwordRules,
    billingAddress: {
      street: {
        required,
        minLength: minLength(3),
      },
      zipcode: {
        required,
      },
      city: {
        required,
      },
      countryId: {
        required,
      },
      company: {
        required: requiredIf(() => state.accountType === "business"),
      },
      department: {
        required: requiredIf(() => state.accountType === "business"),
      },
      phoneNumber: { required },
      customFields: {
        custom_address_field_phone_number_2: {},
      },
    },
    shippingAddress: {
      salutationId: {
        required: requiredIf(() => state.useDifferentShippingAddress),
      },
      firstName: {
        required: requiredIf(() => state.useDifferentShippingAddress),
        minLength: minLength(3),
      },
      lastName: {
        required: requiredIf(() => state.useDifferentShippingAddress),
        minLength: minLength(3),
      },
      company: {
        required: requiredIf(
          () =>
            state.useDifferentShippingAddress &&
            state.accountType === "business",
        ),
      },
      department: {},
      street: {
        required: requiredIf(() => state.useDifferentShippingAddress),
        minLength: minLength(3),
      },
      zipcode: {
        required: requiredIf(() => state.useDifferentShippingAddress),
      },
      city: {
        required: requiredIf(() => state.useDifferentShippingAddress),
      },
      countryId: {
        required: requiredIf(() => state.useDifferentShippingAddress),
      },
      phoneNumber: {
        required: requiredIf(() => state.useDifferentShippingAddress),
      },
    },
  };
});

const $v = useVuelidate(rules, state);
const registerErrors = ref<string[]>([]);
const dataProtection = ref(false);
const dataProtectionError = ref(false);
const { resolveApiErrors, scrollToError } =
  useApiErrorsResolver("account_login");

const emit = defineEmits<{
  (e: "success"): void;
  (e: "error", error: ApiError[]): void;
}>();
const loading = ref(false);
const invokeSubmit = async () => {
  $v.value.$touch();
  registerErrors.value = [];
  const valid = await $v.value.$validate();
  if (!dataProtection.value) {
    dataProtectionError.value = true;
    return;
  }
  if (valid) {
    try {
      loading.value = true;

      // Prepare registration data - only include shipping address if different shipping is selected
      const registrationData = {
        ...state,
        billingAddress: {
          ...state.billingAddress,
          company:
            state.accountType === "business"
              ? state.billingAddress.company ?? undefined
              : undefined,
        },
        ...(state.useDifferentShippingAddress
          ? {}
          : { shippingAddress: undefined }),
        ...(state.accountType === "business"
          ? {
              accountType: "business" as const,
              company: state.billingAddress.company ?? "",
              vatIds: (() => {
                const arr = state.vatIds.filter(
                  (v): v is string => v != null && v !== ""
                );
                return (arr.length > 0 ? arr : [""]) as [string, ...string[]];
              })(),
            }
          : {
              accountType: "private" as const,
              company: null,
              vatIds: null,
            }),
      };

      const response = await register(
        registrationData as unknown as Parameters<ReturnType<typeof useUser>["register"]>[0]
      );
      if (!response.active) {
        pushInfo(t("checkout.messages.checkoutSignInSuccess"));
        await push(formatLink("/"));
      }
      // Refresh session context to update shipping address and VAT
      await refreshSessionContext();
      // Refresh cart to recalculate VAT based on shipping country
      await refreshCart();
      emit("success");
    } catch (err: unknown) {
      // if (err instanceof ApiClientError) {
      const apiError = err as { details?: { errors?: ApiError[] } };
      registerErrors.value = resolveApiErrors(apiError.details?.errors ?? []);
      for (const msg of registerErrors.value) {
        pushError(msg);
      }
      // }
    } finally {
      loading.value = false;
    }
  } else {
    scrollToError();
  }
};
</script>

<template>
  <form
    id="checkout-billing-address"
    class="flex flex-col gap-12"
    name="checkout-billing-address"
    method="post"
    @submit.prevent="invokeSubmit"
  >
    <div class="flex flex-col gap-6">
      <h1
        class="font-marcellus text-2xl md:text-3xl 3xl:text-4xl text-gray-700 mb-4 leading-[30px]"
      >
        {{ $t("checkout.registration.title") }}
      </h1>
      <input
        type="hidden"
        name="securityFormField"
        v-model="state.securityFormField"
      />
      <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
            <label for="register-accountType" class="sr-only">{{
              $t("form.accountType.private")
            }}</label>
            <select
              id="register-accountType"
              v-model="state.accountType"
              name="accountType"
              class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base focus:border-primary"
              :class="[
                $v.accountType.$error ? 'border-red' : 'border-secondary-300',
              ]"
              data-testid="registration-account-type-select"
              @blur="$v.accountType.$touch()"
            >
              <option value="private">
                {{ $t("form.accountType.private") }}
              </option>
              <option value="business">
                {{ $t("form.accountType.business") }}
              </option>
            </select>
            <span
              v-if="$v.accountType.$error"
              class="pt-1 text-sm text-red error-warning"
            >
              {{ $v.accountType.$errors[0].$message }}
            </span>
          </div>
        <div>
          <label for="salutation" class="sr-only">{{
            $t("form.chooseSalutation")
          }}</label>
          <select
            id="salutation"
            v-model="state.salutationId"
            required
            name="salutation"
            autocomplete="on"
            class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base focus:border-primary"
            :class="[
              $v.salutationId.$error ? 'border-red' : 'border-secondary-300',
            ]"
            data-testid="checkout-pi-salutation-select"
            @blur="$v.salutationId.$touch()"
          >
            <option disabled selected value="">
              {{ $t("form.chooseSalutation") }}
            </option>
            <option
              v-for="salutation in getSalutations"
              :key="salutation.id"
              :value="salutation.id"
            >
              {{ salutation.displayName }}
            </option>
          </select>
          <span
            v-if="$v.salutationId.$error"
            class="pt-1 text-sm text-red error-warning"
          >
            {{ $v.salutationId.$errors[0].$message }}
          </span>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label for="first-name" class="sr-only">{{
            $t("form.firstNamePlaceholder")
          }}</label>
          <input
            id="first-name"
            v-model="state.firstName"
            type="text"
            name="first-name"
            :placeholder="$t('form.firstNamePlaceholder')"
            class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base focus:border-primary"
            :class="[
              $v.firstName.$error ? 'border-red' : 'border-secondary-300',
            ]"
            data-testid="checkout-pi-first-name-input"
            @blur="$v.firstName.$touch()"
          />
          <span
            v-if="$v.firstName.$error"
            class="pt-1 text-sm text-red error-warning"
          >
            {{ $v.firstName.$errors[0].$message }}
          </span>
        </div>

        <div>
          <label for="last-name" class="sr-only">{{
            $t("form.lastNamePlaceholder")
          }}</label>
          <input
            id="last-name"
            v-model="state.lastName"
            type="text"
            name="last-name"
            :placeholder="$t('form.lastNamePlaceholder')"
            class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base focus:border-primary"
            :class="[
              $v.lastName.$error ? 'border-red' : 'border-secondary-300',
            ]"
            data-testid="checkout-pi-last-name-input"
            @blur="$v.lastName.$touch()"
          />
          <span
            v-if="$v.lastName.$error"
            class="pt-1 text-sm text-red error-warning"
          >
            {{ $v.lastName.$errors[0].$message }}
          </span>
        </div>
      </div>

      <div class="checkbox-wrap flex items-center gap-3 relative w-full mb-6">
        <input
          id="create-account"
          v-model="state.guest"
          type="checkbox"
          class="custom-checkbox peer absolute left-0"
          name="create-account"
          data-testid="checkout-create-account-checkbox"
        />
        <label
          for="create-account"
          class="font-mullish font-normal select-none text-sm lg:text-base peer-checked:text-purple peer-checked:font-semibold text-grey-500 cursor-pointer relative inset-0 z-1 leading-none block w-full"
        >
          {{ $t("checkout.notCreateAccount") }}
        </label>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label for="register-email-address" class="sr-only">{{
            $t("form.emailPlaceholder")
          }}</label>
          <input
            id="register-email-address"
            v-model="state.email"
            type="email"
            name="checkout-register-email-address"
            :placeholder="$t('form.emailPlaceholder')"
            autocomplete="off"
            class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base focus:border-primary"
            :class="[
              $v.email.$error ? 'border-red' : 'border-secondary-300',
            ]"
            data-testid="checkout-pi-email-input"
            @blur="$v.email.$touch()"
          />
          <span
            v-if="$v.email.$error"
            class="pt-1 text-sm text-red error-warning"
          >
            {{ $v.email.$errors[0].$message }}
          </span>
        </div>

        <div v-if="!state.guest">
          <label for="register-password" class="sr-only">{{
            $t("form.passwordPlaceholder")
          }}</label>
          <input
            id="register-password"
            v-model="state.password"
            autocomplete="off"
            type="password"
            name="password"
            :placeholder="$t('form.passwordPlaceholder')"
            class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base focus:border-primary"
            :class="[
              $v.password.$error ? 'border-red' : 'border-secondary-300',
            ]"
            @blur="$v.password.$touch()"
          />
          <span
            v-if="$v.password.$error"
            class="pt-1 text-sm text-red error-warning"
          >
            {{ $v.password.$errors[0].$message }}
          </span>
          <ul
            class="mt-2 pl-[2px] text-sm text-red list-disc list-inside [&>li]:mr-10 [&>li::marker]:text-base"
          >
            <li>{{ $t("checkout.registration.passwordRequirements1") }}</li>
            <li>{{ $t("checkout.registration.passwordRequirements2") }}</li>
          </ul>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-5">
        <div class="col-span-2 md:col-span-1">
          <ClientOnly>
            <label for="phone1" class="sr-only">{{
              $t("checkout.registration.phone1Placeholder")
            }}</label>
            <SharedIntlTelInput
              v-model="state.billingAddress.phoneNumber"
              :label="$t('checkout.registration.phone1Placeholder')"
              :required="true"
              :error="
                $v.billingAddress.phoneNumber.$error
                  ? $t('checkout.registration.phone1Required')
                  : ''
              "
              id="phone1"
              name="phone1"
              type="tel"
              @blur="$v.billingAddress.phoneNumber.$touch()"
              :placeholder="$t('checkout.registration.phone1Placeholder')"
              :classNames="
                $v.billingAddress.phoneNumber.$error
                  ? 'appearance-none relative block w-full px-3 py-3 border border-red placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base focus:border-primary'
                  : 'appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base border-secondary-300 focus:border-primary'
              "
            />
          </ClientOnly>
          <span
            v-if="$v.billingAddress.phoneNumber.$error"
            class="text-red text-sm mt-1"
          >
            {{ $t("checkout.registration.phone1Required") }}
          </span>
        </div>
        <div class="col-span-2 md:col-span-1">
          <ClientOnly>
            <label for="phone2" class="sr-only">{{
              $t("checkout.registration.phone2Placeholder")
            }}</label>

            <SharedIntlTelInput
              v-model="
                state.billingAddress.customFields
                  .custom_address_field_phone_number_2
              "
              :label="$t('checkout.registration.phone2Placeholder')"
              :required="false"
              id="phone2"
              name="phone2"
              type="tel"
              @blur="
                $v.billingAddress.customFields.custom_address_field_phone_number_2.$touch()
              "
              :placeholder="$t('checkout.registration.phone2Placeholder')"
              classNames="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base border-secondary-300 focus:border-primary"
            />
          </ClientOnly>
        </div>
        <p class="text-sm text-gray-500 col-span-2 -mt-3">
          {{ $t("checkout.registration.phoneNote") }}
        </p>
      </div>
      <div
        v-if="state.accountType === 'business'"
        class="grid grid-cols-1 md:grid-cols-2 gap-5"
      >
        <div>
          <label for="register-vatId" class="sr-only">{{
            $t("form.vatIdPlaceholder")
          }}</label>
          <input
            id="register-vatId"
            v-model="state.vatIds[0]"
            name="vatId"
            class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base focus:border-primary"
            :class="[
              $v.vatIds.$error ? 'border-red' : 'border-secondary-300',
            ]"
            :placeholder="$t('form.vatIdPlaceholder')"
            data-testid="registration-vatid-input"
            @blur="$v.vatIds.$touch()"
          />
        </div>
      </div>
      <div class="addresse-section pt-4 md:pt-8">
        <h2
          class="font-marcellus text-2xl md:text-3xl 3xl:text-4xl text-gray-700 mb-6 leading-[30px]"
        >
          {{ $t("checkout.registration.addressSection") }}
        </h2>
        <div v-if="state.accountType === 'business'" class="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
          <div>
            <label for="register-company" class="sr-only">{{
              $t("form.companyPlaceholder")
            }}</label>
            <input
              id="register-company"
              v-model="state.billingAddress.company"
              name="company"
              type="text"
              autocomplete="company"
              class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base focus:border-primary"
              :class="[
                $v.billingAddress.company.$error
                  ? 'border-red'
                  : 'border-secondary-300',
              ]"
              :placeholder="$t('form.companyPlaceholder')"
              data-testid="registration-company-input"
              @blur="$v.billingAddress.company.$touch()"
            />
            <span
              v-if="$v.billingAddress.company.$error"
              class="pt-1 text-sm text-red error-warning"
            >
              {{ $v.billingAddress.company.$errors[0].$message }}
            </span>
          </div>
          <div>
            <label for="billing-department" class="sr-only">{{
              $t("form.departmentPlaceholder")
            }}</label>
            <input
              id="billing-department"
              v-model="state.billingAddress.department"
              name="billing-department"
              type="text"
              autocomplete="organization-title"
              class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base focus:border-primary"
              :class="[
                $v.billingAddress.department.$error
                  ? 'border-red'
                  : 'border-secondary-300',
              ]"
              :placeholder="$t('form.departmentPlaceholder')"
              @blur="$v.billingAddress.department.$touch()"
            />
            <span
              v-if="$v.billingAddress.department.$error"
              class="pt-1 text-sm text-red error-warning"
            >
              {{ $v.billingAddress.department.$errors[0].$message }}
            </span>
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
         
          <div>
            <label for="street-address" class="sr-only">{{
              $t("form.streetPlaceholder")
            }}</label>
            <input
              id="street-address"
              v-model="state.billingAddress.street"
              type="text"
              required
              name="street-address"
              :placeholder="$t('form.streetPlaceholder')"
              autocomplete="street-address"
              class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base focus:border-primary"
              :class="[
                $v.billingAddress.street.$error
                  ? 'border-red'
                  : 'border-secondary-300',
              ]"
              data-testid="checkout-pi-street-address-input"
              @blur="$v.billingAddress.street.$touch()"
            />
            <span
              v-if="$v.billingAddress.street.$error"
              class="pt-1 text-sm text-red error-warning"
            >
              {{ $v.billingAddress.street.$errors[0].$message }}
            </span>
          </div>

          <div>
            <label for="postal-code" class="sr-only">{{
              $t("form.postalCodePlaceholder")
            }}</label>
            <input
              id="postal-code"
              v-model="state.billingAddress.zipcode"
              type="text"
              required
              name="postal-code"
              :placeholder="$t('form.postalCodePlaceholder')"
              autocomplete="postal-code"
              class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base focus:border-primary"
              :class="[
                $v.billingAddress.zipcode.$error
                  ? 'border-red'
                  : 'border-secondary-300',
              ]"
              data-testid="checkout-pi-zip-code-input"
              @blur="$v.billingAddress.zipcode.$touch()"
            />
            <span
              v-if="$v.billingAddress.zipcode.$error"
              class="pt-1 text-sm text-red error-warning"
            >
              {{ $v.billingAddress.zipcode.$errors[0].$message }}
            </span>
          </div>

          <div>
            <label for="city" class="sr-only">{{
              $t("form.cityPlaceholder")
            }}</label>
            <input
              id="city"
              v-model="state.billingAddress.city"
              type="text"
              required
              name="city"
              :placeholder="$t('form.cityPlaceholder')"
              autocomplete="address-level2"
              class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base focus:border-primary"
              :class="[
                $v.billingAddress.city.$error
                  ? 'border-red'
                  : 'border-secondary-300',
              ]"
              data-testid="checkout-pi-city-input"
              @blur="$v.billingAddress.city.$touch()"
            />
            <span
              v-if="$v.billingAddress.city.$error"
              class="pt-1 text-sm text-red error-warning"
            >
              {{ $v.billingAddress.city.$errors[0].$message }}
            </span>
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
          <SharedCountryStateInput
            v-model:country-id="state.billingAddress.countryId"
            :country-id-validation="$v.billingAddress.countryId"
          />
        </div>
      </div>

      <!-- Shipping Address Section -->
      <div class="shipping-address-section pt-2">
        <div class="checkbox-wrap flex items-center gap-3 relative w-full mb-6">
          <input
            type="checkbox"
            id="use-different-shipping"
            class="custom-checkbox peer absolute left-0"
            name="use-different-shipping"
            v-model="state.useDifferentShippingAddress"
          />
          <label
            for="use-different-shipping"
            class="font-mullish font-normal select-none text-sm lg:text-base peer-checked:text-purple peer-checked:font-semibold text-grey-500 cursor-pointer relative inset-0 z-1 leading-none block w-full"
          >
            {{ $t("checkout.differentBillingAddress") }}
          </label>
        </div>

        <div
          v-if="state.useDifferentShippingAddress"
          class="shipping-address-fields pt-4 md:pt-8"
        >
          <h2
            class="font-marcellus text-2xl md:text-3xl 3xl:text-4xl text-gray-700 mb-6 leading-[30px]"
          >
            {{ $t("checkout.shippingAddressLabel") }}
          </h2>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label for="shipping-salutation" class="sr-only">{{
                $t("form.chooseSalutation")
              }}</label>
              <select
                id="shipping-salutation"
                v-model="state.shippingAddress.salutationId"
                name="shipping-salutation"
                class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base border-secondary-300 focus:border-primary"
                :class="[
                  $v.shippingAddress.salutationId.$error
                    ? 'border-red'
                    : 'border-secondary-300',
                ]"
                @blur="$v.shippingAddress.salutationId.$touch()"
              >
                <option selected value="">
                  {{ $t("form.chooseSalutation") }}
                </option>
                <option
                  v-for="salutation in getSalutations"
                  :key="salutation.id"
                  :value="salutation.id"
                >
                  {{ salutation.displayName }}
                </option>
              </select>
              <span
                v-if="$v.shippingAddress.salutationId.$error"
                class="pt-1 text-sm text-red error-warning"
              >
                {{ $v.shippingAddress.salutationId.$errors[0].$message }}
              </span>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            <div>
              <label for="shipping-first-name" class="sr-only">{{
                $t("form.firstNamePlaceholder")
              }}</label>
              <input
                id="shipping-first-name"
                v-model="state.shippingAddress.firstName"
                name="shipping-first-name"
                type="text"
                autocomplete="given-name"
                class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base border-secondary-300 focus:border-primary"
                :class="[
                  $v.shippingAddress.firstName.$error
                    ? 'border-red'
                    : 'border-secondary-300',
                ]"
                :placeholder="$t('form.firstNamePlaceholder')"
                @blur="$v.shippingAddress.firstName.$touch()"
              />
              <span
                v-if="$v.shippingAddress.firstName.$error"
                class="pt-1 text-sm text-red error-warning"
              >
                {{ $v.shippingAddress.firstName.$errors[0].$message }}
              </span>
            </div>

            <div>
              <label for="shipping-last-name" class="sr-only">{{
                $t("form.lastNamePlaceholder")
              }}</label>
              <input
                id="shipping-last-name"
                v-model="state.shippingAddress.lastName"
                name="shipping-last-name"
                type="text"
                autocomplete="family-name"
                class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base border-secondary-300 focus:border-primary"
                :class="[
                  $v.shippingAddress.lastName.$error
                    ? 'border-red'
                    : 'border-secondary-300',
                ]"
                :placeholder="$t('form.lastNamePlaceholder')"
                @blur="$v.shippingAddress.lastName.$touch()"
              />
              <span
                v-if="$v.shippingAddress.lastName.$error"
                class="pt-1 text-sm text-red error-warning"
              >
                {{ $v.shippingAddress.lastName.$errors[0].$message }}
              </span>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            <div v-if="state.accountType === 'business'">
              <label for="shipping-company" class="sr-only">{{
                $t("form.companyPlaceholder")
              }}</label>
              <input
                id="shipping-company"
                v-model="state.shippingAddress.company"
                name="shipping-company"
                type="text"
                autocomplete="organization"
                class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base border-secondary-300 focus:border-primary"
                :class="[
                  $v.shippingAddress.company.$error
                    ? 'border-red'
                    : 'border-secondary-300',
                ]"
                :placeholder="$t('form.companyPlaceholder')"
                @blur="$v.shippingAddress.company.$touch()"
              />
              <span
                v-if="$v.shippingAddress.company.$error"
                class="pt-1 text-sm text-red error-warning"
              >
                {{ $v.shippingAddress.company.$errors[0].$message }}
              </span>
            </div>

            <div>
              <label for="shipping-department" class="sr-only">{{
                $t("form.departmentPlaceholder")
              }}</label>
              <input
                id="shipping-department"
                v-model="state.shippingAddress.department"
                name="shipping-department"
                type="text"
                autocomplete="organization-title"
                class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base border-secondary-300 focus:border-primary"
                :class="[
                  $v.shippingAddress.department.$error
                    ? 'border-red'
                    : 'border-secondary-300',
                ]"
                :placeholder="$t('form.departmentPlaceholder')"
                @blur="$v.shippingAddress.department.$touch()"
              />
              <span
                v-if="$v.shippingAddress.department.$error"
                class="pt-1 text-sm text-red error-warning"
              >
                {{ $v.shippingAddress.department.$errors[0].$message }}
              </span>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-5 mt-5">
            <div>
              <label for="shipping-street" class="sr-only">{{
                $t("form.streetPlaceholder")
              }}</label>
              <input
                id="shipping-street"
                v-model="state.shippingAddress.street"
                name="shipping-street"
                type="text"
                autocomplete="street-address"
                class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base border-secondary-300 focus:border-primary"
                :class="[
                  $v.shippingAddress.street.$error
                    ? 'border-red'
                    : 'border-secondary-300',
                ]"
                :placeholder="$t('form.streetPlaceholder')"
                @blur="$v.shippingAddress.street.$touch()"
              />
              <span
                v-if="$v.shippingAddress.street.$error"
                class="pt-1 text-sm text-red error-warning"
              >
                {{ $v.shippingAddress.street.$errors[0].$message }}
              </span>
            </div>

            <div>
              <label for="shipping-zipcode" class="sr-only">{{
                $t("form.postalCodePlaceholder")
              }}</label>
              <input
                id="shipping-zipcode"
                v-model="state.shippingAddress.zipcode"
                name="shipping-zipcode"
                type="text"
                autocomplete="postal-code"
                class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base border-secondary-300 focus:border-primary"
                :class="[
                  $v.shippingAddress.zipcode.$error
                    ? 'border-red'
                    : 'border-secondary-300',
                ]"
                :placeholder="$t('form.postalCodePlaceholder')"
                @blur="$v.shippingAddress.zipcode.$touch()"
              />
              <span
                v-if="$v.shippingAddress.zipcode.$error"
                class="pt-1 text-sm text-red error-warning"
              >
                {{ $v.shippingAddress.zipcode.$errors[0].$message }}
              </span>
            </div>

            <div>
              <label for="shipping-city" class="sr-only">{{
                $t("form.cityPlaceholder")
              }}</label>
              <input
                id="shipping-city"
                v-model="state.shippingAddress.city"
                name="shipping-city"
                type="text"
                autocomplete="address-level2"
                class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base border-secondary-300 focus:border-primary"
                :class="[
                  $v.shippingAddress.city.$error
                    ? 'border-red'
                    : 'border-secondary-300',
                ]"
                :placeholder="$t('form.cityPlaceholder')"
                @blur="$v.shippingAddress.city.$touch()"
              />
              <span
                v-if="$v.shippingAddress.city.$error"
                class="pt-1 text-sm text-red error-warning"
              >
                {{ $v.shippingAddress.city.$errors[0].$message }}
              </span>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            <SharedCountryStateInput
              v-model:country-id="state.shippingAddress.countryId"
              :country-id-validation="$v.shippingAddress.countryId"
            />

            <div>
              <SharedIntlTelInput
                v-model="state.shippingAddress.phoneNumber"
                :label="$t('checkout.registration.phone1Placeholder')"
                :required="true"
                :error="
                  $v.shippingAddress.phoneNumber.$error
                    ? $t('checkout.registration.phone1Required')
                    : ''
                "
                id="shipping-phone"
                name="shipping-phone"
                type="tel"
                @blur="$v.shippingAddress.phoneNumber.$touch()"
                :placeholder="$t('checkout.registration.phone1Placeholder')"
                :classNames="
                  $v.shippingAddress.phoneNumber.$error
                    ? 'appearance-none relative block w-full px-3 py-3 border border-red placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base focus:border-primary'
                    : 'appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base border-secondary-300 focus:border-primary'
                "
              />
              <span
                v-if="$v.shippingAddress.phoneNumber.$error"
                class="text-red text-sm mt-1"
              >
                {{ $t("checkout.registration.phone1Required") }}
              </span>
            </div>
          </div>

          
        </div>
      </div>

      <div
        v-if="registerErrors.length"
        class="login-errors text-red focus:ring-primary border-secondary-300 rounded"
        role="alert"
      >
        <ul>
          <li v-for="(error, index) in registerErrors" :key="index">
            {{ error }}
          </li>
        </ul>
      </div>
      <div class="mt-6 lg:mt-2 col-span-4">
        <h2
          class="font-marcellus text-2xl md:text-3xl 3xl:text-4xl text-gray-700 mb-4 leading-[30px]"
        >
          {{ $t("form.dataProtection") }}
        </h2>
        <!-- @@include('../../utils/break.html') -->
        <div
          class="checkbox-wrap flex items-center gap-3 relative w-full mt-4 lg:mt-6"
        >
          <input
            type="checkbox"
            id="register-data-protection"
            class="custom-checkbox peer absolute left-0"
            name="data-protection"
            v-model="dataProtection"
          />
          <label
            for="register-data-protection"
            class="font-mullish font-normal select-none text-sm lg:text-base peer-checked:text-purple peer-checked:font-semibold text-grey-500 cursor-pointer relative inset-0 z-1 leading-none block w-full"
          >
            {{ $t("form.dataProtectionPlaceholder") }}
            <NuxtLink href="/informationen/datenschutz/" class="text-primary hover:text-primary-dark underline">
              {{ $t("form.dataProtectionLink") }}
            </NuxtLink>
            {{ $t("form.dataProtectionPlaceholder2") }}
          </label>
        </div>
        <span
          v-if="dataProtectionError && !dataProtection"
          class="pt-1 text-sm text-red"
        >
          {{ $t("form.dataProtectionError") }}
        </span>
      </div>
    </div>

    <button
      type="submit"
      class="btn btn-primary btn-full px-14 w-full sm:w-fit"
      data-testid="checkout-pi-submit-button"
      :disabled="loading"
    >
      <span class="my-auto text-nowrap">
        {{ $t("checkout.continueToPayment") }}
      </span>
    </button>
  </form>
</template>
